// src/pages/AnalyticsPageV2.tsx - March 15, 2026
// V2 Analytics Detail Page - Uses Netlify Functions (new architecture)
// Auto-generated from AnalyticsPage.tsx by create-v2-pages.ps1
// DO NOT edit UI logic here. Edit AnalyticsPage.tsx, then re-run this script.
// src/pages/AnalyticsPage.tsx
// Detailed analytics: Sales Detail (item comparison) + AR Detail (customer aging with flags)
// Two tabs, date range pickers with Hebrew toggle, multi-filter system

import { useState, useMemo, Fragment } from 'react';
import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import {
  ArrowLeft, Search, RefreshCw, ChevronDown,
  DollarSign, Hash, Eye, BarChart3,
  X, CreditCard, FileText, Mail, Loader2, BookOpen,
} from 'lucide-react';
import { useAuthContext } from '../hooks/useAuth';
import { apiV2 as api } from '../services/apiV2';
import { adminApi } from '../services/adminApi';
import CustomerDocsModal from '../components/CustomerDocsModal';
import CustomerLedgerModal from '../components/CustomerLedgerModal';
import ARInvoiceLines, { InvoicePdfButtons } from '../components/ARInvoiceLines';
import { buildStatementPdf, pdfBase64, skippedOrUnderpaid } from '../lib/customerDocs';
import { DEFAULT_TEMPLATE, renderTemplate, bodyToHtml } from '../lib/customerDocTemplate';
import type { ARCustomerDetail, AnalyticsFilters } from '../types';
import HebrewRangePicker from '../components/HebrewDatePicker';
import DateField from '../components/DateField';
import { FilterText } from './analytics/salesFilterBar';
import { subcategoryOptions } from './analytics/itemSubcategories';
import { ArRollup, type ArRollupDim } from './analytics/ArRollup';
import { useFeatureAccess } from '../hooks/useFeatureAccess';

// ══════════════════════════════════════════════════════════════
// Constants & Helpers
// ══════════════════════════════════════════════════════════════

const currentYear = new Date().getFullYear();
const todayStr = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; };
const yearStartStr = (y = currentYear) => `${y}-01-01`;
const fmtCur = (v: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);
const fmtNum = (v: number) => new Intl.NumberFormat('en-US').format(Math.round(v));
const fmtDate = (d: string | null) => { if (!d) return '—'; try { return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); } catch { return d; } };

// Hebrew month names for the date picker

interface AnalyticsPageProps { onBack?: () => void; }

// ══════════════════════════════════════════════════════════════
// Date Range Picker with Hebrew Toggle
// ══════════════════════════════════════════════════════════════

function DateRangePicker({ label, startDate, endDate, onStartChange, onEndChange }: {
  label: string; startDate: string; endDate: string;
  onStartChange: (v: string) => void; onEndChange: (v: string) => void;
}) {
  const [showHebrew, setShowHebrew] = useState(false);
 
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2">
        <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">{label}</span>
        <button onClick={() => setShowHebrew(!showHebrew)}
          className={`text-[10px] px-1.5 py-0.5 rounded border transition-all ${showHebrew ? 'border-blue-500 text-blue-400 bg-blue-500/10' : 'border-slate-600 text-slate-500 hover:text-slate-300'}`}
        >{showHebrew ? 'Hebrew Calendar' : 'HEB'}</button>
      </div>
 
      {!showHebrew ? (
        <div className="flex items-center gap-1.5">
          <DateField value={startDate} onChange={onStartChange} className="w-[130px]" />
          <span className="text-slate-600 text-xs">→</span>
          <DateField value={endDate} onChange={onEndChange} className="w-[130px]" />
        </div>
      ) : (
        <HebrewRangePicker
          onApply={(startGreg, endGreg) => {
            onStartChange(startGreg);
            onEndChange(endGreg);
          }}
        />
      )}
    </div>
  );
}


// ══════════════════════════════════════════════════════════════
// Filter Select
// ══════════════════════════════════════════════════════════════

function FilterSelect({ label, value, onChange, options, placeholder = 'All' }: {
  label: string; value: string; onChange: (v: string) => void;
  options: { value: string; label: string }[]; placeholder?: string;
}) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">{label}</span>
      <div className="relative">
        <select value={value} onChange={e => onChange(e.target.value)}
          className="appearance-none px-2.5 py-1.5 pr-7 rounded-md bg-slate-800/50 border border-slate-700/50 text-xs text-white focus:outline-none focus:ring-1 focus:ring-blue-500/50 cursor-pointer min-w-[120px]">
          <option value="">{placeholder}</option>
          {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <ChevronDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// Display Toggle (Qty / Dollars / Both)
// ══════════════════════════════════════════════════════════════

type DisplayMode = 'both' | 'qty' | 'dollars';

function DisplayToggle({ mode, onChange }: { mode: DisplayMode; onChange: (m: DisplayMode) => void }) {
  const opts: { value: DisplayMode; label: string; icon: typeof Hash }[] = [
    { value: 'both', label: 'Both', icon: Eye },
    { value: 'qty', label: 'Qty', icon: Hash },
    { value: 'dollars', label: '$', icon: DollarSign },
  ];
  return (
    <div className="flex rounded-md border border-slate-700/50 overflow-hidden">
      {opts.map(o => (
        <button key={o.value} onClick={() => onChange(o.value)}
          className={`px-2.5 py-1.5 text-xs flex items-center gap-1 transition-all ${mode === o.value ? 'bg-blue-600 text-white' : 'bg-slate-800/50 text-slate-400 hover:text-white'}`}>
          <o.icon size={11} />{o.label}
        </button>
      ))}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// Change Badge
// ══════════════════════════════════════════════════════════════

function ChangeBadge({ value }: { value: number | null }) {
  if (value === null) return <span className="text-slate-600 text-xs">NEW</span>;
  if (value === -100) return <span className="text-red-400 text-xs">DROPPED</span>;
  const color = value > 0 ? 'text-emerald-400' : value < 0 ? 'text-red-400' : 'text-slate-400';
  return <span className={`text-xs font-medium ${color}`}>{value > 0 ? '+' : ''}{value.toFixed(1)}%</span>;
}

// ══════════════════════════════════════════════════════════════
// Flag Badges for AR
// ══════════════════════════════════════════════════════════════

function FlagBadges({ row }: { row: ARCustomerDetail }) {
  const badges = [];
  if (row.hasSkipped) badges.push({ label: 'Skipped', color: 'bg-red-500/20 text-red-300 border-red-500/30' });
  if (row.shortpaidCount > 0) badges.push({ label: `Short (${row.shortpaidCount})`, color: 'bg-amber-500/20 text-amber-300 border-amber-500/30' });
  if (row.overpaidCount > 0) badges.push({ label: `Over (${row.overpaidCount})`, color: 'bg-blue-500/20 text-blue-300 border-blue-500/30' });
  if (row.hasPostDated) badges.push({ label: 'Post-dated', color: 'bg-purple-500/20 text-purple-300 border-purple-500/30' });
  if (row.noRecentPurchases) badges.push({ label: 'No recent', color: 'bg-slate-500/20 text-slate-300 border-slate-500/30' });
  if (!badges.length) return null;
  return (
    <div className="flex flex-wrap gap-1">
      {badges.map(b => <span key={b.label} className={`text-[10px] px-1.5 py-0.5 rounded border ${b.color}`}>{b.label}</span>)}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// Sales Detail Tab
// ══════════════════════════════════════════════════════════════

export function SalesDetailTab({ filters }: { filters?: AnalyticsFilters }) {
  const { hasFeature } = useAuthContext();
  const showAvgPrice = hasFeature('showCostProfit');
  const [p1Start, setP1Start] = useState(yearStartStr());
  const [p1End, setP1End] = useState(todayStr());
  const [p2Start, setP2Start] = useState(yearStartStr(currentYear - 1));
  const [p2End, setP2End] = useState(() => { const d = todayStr().split('-'); d[0] = String(currentYear - 1); return d.join('-'); });
  const [salesman, setSalesman] = useState('');
  const [customer, setCustomer] = useState('');
  const [driver, setDriver] = useState('');
  const [category, setCategory] = useState('');
  // Extended filters (agent-side additions to analytics-sales-detail):
  // salesman1 (customer's Salesman1), customerName (substring), item (itemNo
  // exact or description substring), itemCategory (item category exact).
  const [salesman1, setSalesman1] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [item, setItem] = useState('');
  const [itemCategory, setItemCategory] = useState('');
  const [itemSubcategory, setItemSubcategory] = useState('');
  const [displayMode, setDisplayMode] = useState<DisplayMode>('both');
  const [searchTerm, setSearchTerm] = useState('');
  // sortField null = preserve server order (ItemNo ASC). User can click a
  // column header to engage client-side sort on loaded rows.
  const [sortField, setSortField] = useState<'p1Dollars' | 'p1Qty' | 'dollarsChange' | 'qtyChange' | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const PAGE_SIZE = 50;

  // When rendered without a filters prop (the Analytics hub does this),
  // fetch the dropdown data ourselves — same query key as AnalyticsPageV2
  // so the cache is shared.
  const { data: fetchedFilters } = useQuery({
    queryKey: ['v2-analyticsFilters'],
    queryFn: () => api.getAnalyticsFilters(),
    staleTime: 5 * 60 * 1000,
    enabled: !filters,
  });
  const f = filters ?? fetchedFilters;
  const subcats = subcategoryOptions(f, itemCategory);

  // useInfiniteQuery: server returns 50 items per page sorted by ItemNo ASC.
  // Each page response also carries the period summary (same on every page);
  // we read it off the first page. Load More appends the next page.
  const { data: infData, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage, refetch } = useInfiniteQuery({
    queryKey: ['v2-salesDetail', p1Start, p1End, p2Start, p2End, salesman, customer, driver, category, salesman1, customerName, item, itemCategory, itemSubcategory],
    initialPageParam: 0,
    queryFn: ({ pageParam }) => api.getSalesDetail({
      p1Start, p1End, p2Start, p2End,
      salesman: salesman || undefined,
      customer: customer || undefined,
      driver: driver || undefined,
      category: category || undefined,
      salesman1: salesman1 || undefined,
      customerName: customerName || undefined,
      item: item || undefined,
      itemCategory: itemCategory || undefined,
      itemSubcategory: itemSubcategory || undefined,
      limit: String(PAGE_SIZE),
      offset: String(pageParam),
    }),
    getNextPageParam: (lastPage) => {
      const off = lastPage.offset ?? 0;
      const lim = lastPage.limit ?? PAGE_SIZE;
      const total = lastPage.totalCount ?? lastPage.items.length;
      const next = off + lim;
      return next < total ? next : undefined;
    },
    retry: 1,
  });

  // Page 1 carries the summary (same on every page); items = concat of all loaded pages.
  const data = infData?.pages[0];
  const loadedItems = useMemo(
    () => infData?.pages.flatMap(p => p.items) ?? [],
    [infData],
  );

  // Sort/search apply to loaded rows only. Full server-side sort + filter
  // ships in a later release (task #60).
  const filteredItems = useMemo(() => {
    let items = loadedItems;
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      items = items.filter(i => i.itemNo.toLowerCase().includes(s) || i.description.toLowerCase().includes(s));
    }
    if (sortField !== null) {
      items = [...items].sort((a, b) => {
        const aVal = a[sortField] ?? 0;
        const bVal = b[sortField] ?? 0;
        return sortDir === 'desc' ? (bVal as number) - (aVal as number) : (aVal as number) - (bVal as number);
      });
    }
    return items;
  }, [loadedItems, searchTerm, sortField, sortDir]);

  const totalCount = data?.totalCount ?? loadedItems.length;

  const handleSort = (field: typeof sortField) => {
    if (sortField === field) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortField(field); setSortDir('desc'); }
  };

  const showQty = displayMode === 'qty' || displayMode === 'both';
  const showDol = displayMode === 'dollars' || displayMode === 'both';

  return (
    <div className="space-y-4">
      {/* Filters row */}
      <div className="flex flex-wrap items-end gap-3 p-4 rounded-xl bg-slate-800/20 border border-slate-700/30">
        <DateRangePicker label={`Period 1 (${currentYear})`} startDate={p1Start} endDate={p1End} onStartChange={setP1Start} onEndChange={setP1End} />
        <DateRangePicker label={`Period 2 (${currentYear-1})`} startDate={p2Start} endDate={p2End} onStartChange={setP2Start} onEndChange={setP2End} />
        <div className="h-px w-px" /> {/* spacer */}
        {f && <>
          <FilterSelect label="Salesman" value={salesman} onChange={setSalesman} options={f.salesmen.map(s => ({ value: s, label: s }))} />
          <FilterSelect label="Salesman 1" value={salesman1} onChange={setSalesman1} options={(f.salesmen1 ?? []).map(s => ({ value: s, label: s }))} />
          <FilterSelect label="Category" value={category} onChange={setCategory} options={f.categories.map(c => ({ value: c, label: c }))} />
          <FilterSelect label="Route" value={driver} onChange={setDriver} options={f.drivers.map(d => ({ value: d, label: d }))} />
          <FilterSelect label="Customer" value={customer} onChange={setCustomer}
            options={f.customers.map(c => ({ value: String(c.code), label: `${c.code} - ${c.name}` }))} placeholder="All Customers" />
          <FilterText label="Customer Name" value={customerName} onChange={setCustomerName} placeholder="Name contains…" />
          <FilterText label="Item" value={item} onChange={setItem} placeholder="Item # or description…" />
          <FilterSelect label="Item Category" value={itemCategory} onChange={v => { setItemCategory(v); setItemSubcategory(''); }} options={(f.itemCategories ?? []).map(c => ({ value: c, label: c }))} />
          {subcats && (
            <FilterSelect label="Item Subcategory" value={itemSubcategory} onChange={setItemSubcategory} options={subcats.map(c => ({ value: c, label: c }))} />
          )}
        </>}
        <div className="flex-1" />
        <DisplayToggle mode={displayMode} onChange={setDisplayMode} />
        <button onClick={() => refetch()} disabled={isLoading}
          className="p-2 rounded-md bg-slate-700/50 border border-slate-600/50 text-slate-400 hover:text-white transition-all disabled:opacity-50">
          <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Summary cards */}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {showDol && <div className="p-3 rounded-lg bg-slate-800/30 border border-slate-700/30">
            <p className="text-xs text-slate-500">P1 Sales</p><p className="text-lg font-bold text-white">{fmtCur(data.p1Total)}</p>
          </div>}
          {showDol && <div className="p-3 rounded-lg bg-slate-800/30 border border-slate-700/30">
            <p className="text-xs text-slate-500">P2 Sales</p><p className="text-lg font-bold text-white">{fmtCur(data.p2Total)}</p>
          </div>}
          {showQty && <div className="p-3 rounded-lg bg-slate-800/30 border border-slate-700/30">
            <p className="text-xs text-slate-500">P1 Qty</p><p className="text-lg font-bold text-white">{fmtNum(data.p1QtyTotal)}</p>
          </div>}
          {showQty && <div className="p-3 rounded-lg bg-slate-800/30 border border-slate-700/30">
            <p className="text-xs text-slate-500">P2 Qty</p><p className="text-lg font-bold text-white">{fmtNum(data.p2QtyTotal)}</p>
          </div>}
        </div>
      )}

      {/* Search */}
      <div className="relative w-64">
        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
        <input type="text" placeholder="Search items..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
          className="w-full pl-8 pr-3 py-1.5 rounded-md bg-slate-800/50 border border-slate-700/50 text-xs text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-blue-500/50" />
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex items-center justify-center h-40"><RefreshCw className="w-6 h-6 text-blue-400 animate-spin" /></div>
      ) : (
        <div className="rounded-xl border border-slate-700/50 bg-slate-800/30 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-slate-700/50 bg-slate-800/50">
                  {/* Sticky identifying column — opaque bg so scrolled content doesn't show through */}
                  <th className="sticky left-0 z-10 bg-slate-800 px-3 py-2.5 text-left text-slate-400 font-medium">Item</th>
                  <th className="px-3 py-2.5 text-left text-slate-400 font-medium">Description</th>
                  {showQty && <th className="px-3 py-2.5 text-right text-slate-400 font-medium cursor-pointer hover:text-white" onClick={() => handleSort('p1Qty')}>P1 Qty {sortField === 'p1Qty' && (sortDir === 'desc' ? '↓' : '↑')}</th>}
                  {showQty && <th className="px-3 py-2.5 text-right text-slate-400 font-medium">P2 Qty</th>}
                  {showQty && <th className="px-3 py-2.5 text-right text-slate-400 font-medium cursor-pointer hover:text-white" onClick={() => handleSort('qtyChange')}>Qty Δ {sortField === 'qtyChange' && (sortDir === 'desc' ? '↓' : '↑')}</th>}
                  {showDol && <th className="px-3 py-2.5 text-right text-slate-400 font-medium cursor-pointer hover:text-white" onClick={() => handleSort('p1Dollars')}>P1 $ {sortField === 'p1Dollars' && (sortDir === 'desc' ? '↓' : '↑')}</th>}
                  {showDol && <th className="px-3 py-2.5 text-right text-slate-400 font-medium">P2 $</th>}
                  {showDol && <th className="px-3 py-2.5 text-right text-slate-400 font-medium cursor-pointer hover:text-white" onClick={() => handleSort('dollarsChange')}>$ Δ {sortField === 'dollarsChange' && (sortDir === 'desc' ? '↓' : '↑')}</th>}
                  {showDol && showAvgPrice && <th className="px-3 py-2.5 text-right text-slate-400 font-medium">Avg P1</th>}
                  {showDol && showAvgPrice && <th className="px-3 py-2.5 text-right text-slate-400 font-medium">Avg P2</th>}
                </tr>
              </thead>
              <tbody>
                {filteredItems.map(item => (
                  <tr key={item.itemNo} className="group border-b border-slate-800/50 hover:bg-slate-700/20 transition-colors">
                    <td className="sticky left-0 z-10 bg-slate-900 group-hover:bg-slate-800 px-3 py-2 font-mono text-slate-300">{item.itemNo}</td>
                    <td className="px-3 py-2 text-white max-w-[200px] truncate">{item.description}</td>
                    {showQty && <td className="px-3 py-2 text-right text-slate-300">{fmtNum(item.p1Qty)}</td>}
                    {showQty && <td className="px-3 py-2 text-right text-slate-500">{fmtNum(item.p2Qty)}</td>}
                    {showQty && <td className="px-3 py-2 text-right"><ChangeBadge value={item.qtyChange} /></td>}
                    {showDol && <td className="px-3 py-2 text-right text-slate-300">{fmtCur(item.p1Dollars)}</td>}
                    {showDol && <td className="px-3 py-2 text-right text-slate-500">{fmtCur(item.p2Dollars)}</td>}
                    {showDol && <td className="px-3 py-2 text-right"><ChangeBadge value={item.dollarsChange} /></td>}
                    {showDol && showAvgPrice && <td className="px-3 py-2 text-right text-slate-400">{item.avgPriceP1 > 0 ? `$${item.avgPriceP1.toFixed(2)}` : '—'}</td>}
                    {showDol && showAvgPrice && <td className="px-3 py-2 text-right text-slate-500">{item.avgPriceP2 > 0 ? `$${item.avgPriceP2.toFixed(2)}` : '—'}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {filteredItems.length === 0 && !isLoading && (
            <div className="text-center py-8 text-slate-500 text-sm">No items found for the selected period and filters</div>
          )}
          <div className="px-3 py-2 border-t border-slate-700/30 flex items-center justify-between gap-3">
            <div className="text-xs text-slate-500">
              Showing {filteredItems.length} of {totalCount} items
              {searchTerm && filteredItems.length !== loadedItems.length && ` (filtered from ${loadedItems.length} loaded)`}
            </div>
            {hasNextPage && (
              <button
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
                className="px-3 py-1.5 rounded-md text-xs font-medium bg-blue-500/10 border border-blue-500/30 text-blue-300 hover:bg-blue-500/20 hover:text-white transition-all disabled:opacity-50"
              >
                {isFetchingNextPage ? 'Loading…' : `Load more (+${PAGE_SIZE})`}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// AR Detail Tab
// ══════════════════════════════════════════════════════════════

export function ARDetailTab({ filters }: { filters?: AnalyticsFilters }) {
  const [salesman, setSalesman] = useState('');
  const [category, setCategory] = useState('');
  const [flag, setFlag] = useState('');
  const [aging, setAging] = useState('');
  const [showInactive, setShowInactive] = useState('active');
  const [area, setArea] = useState('');
  const [minBal, setMinBal] = useState('');
  const [maxBal, setMaxBal] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<number | null>(null);
  const [docsFor, setDocsFor] = useState<{ code: number; name: string } | null>(null);
  const [ledgerFor, setLedgerFor] = useState<{ code: number; name: string } | null>(null);
  const [openInvoice, setOpenInvoice] = useState<string | null>(null);
  const [groupBusy, setGroupBusy] = useState(false);
  const [groupMsg, setGroupMsg] = useState<string | null>(null);
  const { branding } = useAuthContext();
  const company = branding?.company_name || 'Statement';
  const [sortField, setSortField] = useState<'balance' | 'avgAging' | 'over90' | 'name'>('balance');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  // A/R by Salesman / Area / Category rollup (flag analytics.arsalesman).
  const { canSee } = useFeatureAccess();
  const showRollup = canSee('analytics.arsalesman');
  const [rollupDim, setRollupDim] = useState<ArRollupDim>('salesman');
  const [rollupKey, setRollupKey] = useState('');

  // The Analytics hub renders this tab without a filters prop, which used to
  // hide the Salesman / Category dropdowns. Fetch them (shared cache key) —
  // behind the same flag so tenants without it see the tab unchanged.
  const { data: fetchedFilters } = useQuery({
    queryKey: ['v2-analyticsFilters'],
    queryFn: () => api.getAnalyticsFilters(),
    staleTime: 5 * 60 * 1000,
    enabled: !filters && showRollup,
  });
  const arFilters = filters ?? (showRollup ? fetchedFilters : undefined);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['v2-arDetail', salesman, category, flag, aging, showInactive],
    queryFn: () => api.getARDetail({ salesman: salesman || undefined, category: category || undefined, flag: flag || undefined, aging: aging || undefined, showInactive: showInactive || undefined }),
    retry: 1,
  });

  const { data: drilldown } = useQuery({
    queryKey: ['v2-arDrilldown', selectedCustomer],
    queryFn: () => api.getARDrilldown(selectedCustomer!),
    enabled: selectedCustomer !== null,
    retry: 1,
  });

  const areaOptions = useMemo(
    () => Array.from(new Set((data?.customers ?? []).map(c => (c.area || '').trim()).filter(Boolean))).sort(),
    [data],
  );

  const filteredCustomers = useMemo(() => {
    if (!data?.customers) return [];
    let custs = data.customers;
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      custs = custs.filter(c => c.name.toLowerCase().includes(s) || String(c.code).includes(s));
    }
    if (area) custs = custs.filter(c => (c.area || '').trim() === area);
    if (showRollup && rollupKey) {
      const keyOf = (c: ARCustomerDetail) => ((rollupDim === 'salesman' ? c.salesman : rollupDim === 'area' ? c.area : c.category) || '').trim() || '(none)';
      custs = custs.filter(c => keyOf(c) === rollupKey);
    }
    if (minBal !== '') { const m = parseFloat(minBal); if (!isNaN(m)) custs = custs.filter(c => c.balance >= m); }
    if (maxBal !== '') { const m = parseFloat(maxBal); if (!isNaN(m)) custs = custs.filter(c => c.balance <= m); }
    // Copy before sort so we don't mutate the query cache array in place.
    return [...custs].sort((a, b) => {
      if (sortField === 'name') return sortDir === 'desc' ? b.name.localeCompare(a.name) : a.name.localeCompare(b.name);
      return sortDir === 'desc' ? (b[sortField] as number) - (a[sortField] as number) : (a[sortField] as number) - (b[sortField] as number);
    });
  }, [data, searchTerm, area, minBal, maxBal, sortField, sortDir, showRollup, rollupDim, rollupKey]);

  const handleSort = (field: typeof sortField) => {
    if (sortField === field) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortField(field); setSortDir('desc'); }
  };

  const summary = data?.summary;

  // Email a statement to every filtered customer that has a balance + an email
  // on file (skips the rest). One drilldown + one send per customer.
  async function groupSendStatements() {
    const targets = filteredCustomers.filter((c) => c.balance > 0);
    if (targets.length === 0) { setGroupMsg('No customers with a balance in the current filter.'); return; }
    if (targets.length > 40 && !window.confirm(`Email statements to ${targets.length} customers?`)) return;
    setGroupBusy(true); setGroupMsg(null);
    const [subCfg, bodyCfg] = await Promise.all([
      adminApi.getConfig('customer_docs.email_subject').catch(() => null),
      adminApi.getConfig('customer_docs.email_body').catch(() => null),
    ]);
    const tpl = { subject: subCfg?.value || DEFAULT_TEMPLATE.subject, body: bodyCfg?.value || DEFAULT_TEMPLATE.body };
    let sent = 0, noEmail = 0, failed = 0;
    for (const c of targets) {
      try {
        const dd = await api.getARDrilldown(c.code);
        const em = dd.customer.email?.trim();
        if (!em) { noEmail++; continue; }
        const totalDue = dd.invoices.reduce((s, i) => s + i.balance, 0);
        const flagged = skippedOrUnderpaid(dd.invoices).map((i) => `Invoice ${i.invoice} (${i.date.slice(0, 10)}) — balance ${i.balance.toFixed(2)}`);
        const t = renderTemplate(tpl, { customerName: dd.customer.name, companyName: company, balance: totalDue.toFixed(2), skippedLines: flagged });
        const pdf = pdfBase64(buildStatementPdf({ customer: { code: dd.customer.code, name: dd.customer.name, email: em, balance: dd.customer.balance }, invoices: dd.invoices }, company));
        await api.sendCustomerDoc({ to: [em], subject: t.subject, html: bodyToHtml(t.body), attachments: [{ filename: `Statement-${c.code}.pdf`, contentBase64: pdf }] });
        sent++;
      } catch { failed++; }
    }
    setGroupBusy(false);
    setGroupMsg(`Statements: ${sent} sent · ${noEmail} no email · ${failed} failed.`);
  }

  return (
    <div className="space-y-4">
      {docsFor && <CustomerDocsModal code={docsFor.code} name={docsFor.name} onClose={() => setDocsFor(null)} />}
      {ledgerFor && <CustomerLedgerModal code={ledgerFor.code} name={ledgerFor.name} onClose={() => setLedgerFor(null)} />}
      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3 p-4 rounded-xl bg-slate-800/20 border border-slate-700/30">
        {arFilters && <>
          <FilterSelect label="Salesman" value={salesman} onChange={setSalesman} options={arFilters.salesmen.map(s => ({ value: s, label: s }))} />
          <FilterSelect label="Category" value={category} onChange={setCategory} options={arFilters.categories.map(c => ({ value: c, label: c }))} />
        </>}
        <FilterSelect label="Flag" value={flag} onChange={setFlag} options={[
          { value: 'skipped', label: 'Skipped Invoices' },
          { value: 'shortpaid', label: 'Short Paid' },
          { value: 'overpaid', label: 'Over Paid' },
          { value: 'postdated', label: 'Post-dated Payments' },
          { value: 'norecent', label: 'No Recent Purchases' },
        ]} placeholder="All Flags" />
        <FilterSelect label="Aging" value={aging} onChange={setAging} options={[
          { value: 'current', label: 'Current (0-30)' },
          { value: '31-60', label: '31-60 Days' },
          { value: '61-90', label: '61-90 Days' },
          { value: 'over90', label: 'Over 90 Days' },
        ]} placeholder="All Aging" />
        <FilterSelect label="Status" value={showInactive} onChange={setShowInactive} options={[
          { value: 'active', label: 'Active Only' },
          { value: 'inactive', label: 'Inactive Only' },
          { value: 'all', label: 'All Customers' },
        ]} placeholder="Active Only" />
        {areaOptions.length > 0 && (
          <FilterSelect label="Area" value={area} onChange={setArea}
            options={areaOptions.map(a => ({ value: a, label: a }))} placeholder="All Areas" />
        )}
        <div>
          <label className="block text-[11px] text-slate-500 mb-1">Balance</label>
          <div className="flex items-center gap-1">
            <input type="number" value={minBal} onChange={e => setMinBal(e.target.value)} placeholder="Min"
              className="w-20 px-2 py-1.5 rounded-md bg-slate-700/50 border border-slate-600/50 text-sm text-white placeholder:text-slate-500" />
            <span className="text-slate-500 text-xs">–</span>
            <input type="number" value={maxBal} onChange={e => setMaxBal(e.target.value)} placeholder="Max"
              className="w-20 px-2 py-1.5 rounded-md bg-slate-700/50 border border-slate-600/50 text-sm text-white placeholder:text-slate-500" />
          </div>
        </div>
        <div className="flex-1" />
        <button onClick={groupSendStatements} disabled={groupBusy}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-blue-600/80 hover:bg-blue-600 text-white text-sm disabled:opacity-50"
          title="Email a statement to every filtered customer that has a balance + an email on file">
          {groupBusy ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />} Email statements
        </button>
        <button onClick={() => refetch()} disabled={isLoading}
          className="p-2 rounded-md bg-slate-700/50 border border-slate-600/50 text-slate-400 hover:text-white transition-all disabled:opacity-50">
          <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} />
        </button>
      </div>
      {groupMsg && (
        <div className="text-sm px-3 py-2 rounded-lg bg-slate-800/40 border border-slate-700/40 text-slate-200">{groupMsg}</div>
      )}

      {/* Summary cards — Aging */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <div className="p-3 rounded-lg bg-slate-800/30 border border-slate-700/30">
            <p className="text-xs text-slate-500">Total AR</p><p className="text-lg font-bold text-white">{fmtCur(summary.totalAR)}</p>
          </div>
          <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
            <p className="text-xs text-emerald-400/70">Current</p><p className="text-lg font-bold text-emerald-300">{fmtCur(summary.totalCurrent)}</p>
          </div>
          <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
            <p className="text-xs text-blue-400/70">31-60</p><p className="text-lg font-bold text-blue-300">{fmtCur(summary.totalOver30)}</p>
          </div>
          <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
            <p className="text-xs text-amber-400/70">61-90</p><p className="text-lg font-bold text-amber-300">{fmtCur(summary.totalOver60)}</p>
          </div>
          <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20">
            <p className="text-xs text-red-400/70">Over 90</p><p className="text-lg font-bold text-red-300">{fmtCur(summary.totalOver90)}</p>
          </div>
          <div className="p-3 rounded-lg bg-slate-800/30 border border-slate-700/30">
            <p className="text-xs text-slate-500">Flags</p>
            <div className="flex gap-2 mt-1 text-[10px] flex-wrap">
              {summary.skippedCount > 0 && <span className="text-red-300">{summary.skippedCount} skip</span>}
              {summary.shortpaidCount > 0 && <span className="text-amber-300">{summary.shortpaidCount} short</span>}
              {summary.overpaidCount > 0 && <span className="text-blue-300">{summary.overpaidCount} over</span>}
              {summary.postDatedCount > 0 && <span className="text-purple-300">{summary.postDatedCount} post</span>}
            </div>
          </div>
        </div>
      )}

      {/* Summary cards — Flag Amounts */}
      {summary && (summary.skippedCount > 0 || summary.shortpaidCount > 0 || summary.overpaidCount > 0 || summary.postDatedCount > 0) && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {summary.skippedCount > 0 && (
            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20">
              <p className="text-xs text-red-400/70">Skipped ({summary.skippedCount})</p>
              <p className="text-lg font-bold text-red-300">{fmtCur(summary.skippedAmount)}</p>
            </div>
          )}
          {summary.shortpaidCount > 0 && (
            <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
              <p className="text-xs text-amber-400/70">Short Paid ({summary.shortpaidCount})</p>
              <p className="text-lg font-bold text-amber-300">{fmtCur(summary.shortpaidAmount)}</p>
            </div>
          )}
          {summary.overpaidCount > 0 && (
            <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
              <p className="text-xs text-blue-400/70">Over Paid ({summary.overpaidCount})</p>
              <p className="text-lg font-bold text-blue-300">{fmtCur(Math.abs(summary.overpaidAmount))}</p>
            </div>
          )}
          {summary.postDatedCount > 0 && (
            <div className="p-3 rounded-lg bg-purple-500/10 border border-purple-500/20">
              <p className="text-xs text-purple-400/70">Post-dated ({summary.postDatedCount})</p>
              <p className="text-lg font-bold text-purple-300">{fmtCur(summary.postDatedAmount)}</p>
            </div>
          )}
        </div>
      )}

      {/* Search */}
      <div className="relative w-64">
        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
        <input type="text" placeholder="Search customers..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
          className="w-full pl-8 pr-3 py-1.5 rounded-md bg-slate-800/50 border border-slate-700/50 text-xs text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-blue-500/50" />
      </div>

      {/* Drilldown Panel */}
      {selectedCustomer !== null && drilldown && (
        <div className="rounded-xl border border-blue-500/30 bg-blue-500/5 p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-white">{drilldown.customer.name} (#{drilldown.customer.code})</h3>
            <div className="flex items-center gap-2">
              <button onClick={() => setDocsFor({ code: drilldown.customer.code, name: drilldown.customer.name })}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-blue-600/80 hover:bg-blue-600 text-white text-xs">
                <FileText size={13} /> Documents
              </button>
              {canSee('analytics.arledger') && (
                <button onClick={() => setLedgerFor({ code: drilldown.customer.code, name: drilldown.customer.name })}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-slate-700/80 hover:bg-slate-600 text-white text-xs">
                  <BookOpen size={13} /> Ledger
                </button>
              )}
              <button onClick={() => setSelectedCustomer(null)} className="text-slate-400 hover:text-white"><X size={16} /></button>
            </div>
          </div>
          <div className="flex flex-wrap gap-4 text-xs text-slate-400">
            <span>Balance: <span className="text-white font-medium">{fmtCur(drilldown.customer.balance)}</span></span>
            <span>Credit Limit: {fmtCur(drilldown.customer.creditLimit)}</span>
            <span>Avg Aging: {drilldown.customer.avgAging} days</span>
            <span>Last Payment: {fmtDate(drilldown.customer.lastPaymentDate)} ({fmtCur(drilldown.customer.lastPaymentAmount)})</span>
          </div>
          {/* Invoice list */}
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr className="border-b border-slate-700/30">
                <th className="px-2 py-1.5 text-left text-slate-400">Invoice</th>
                <th className="px-2 py-1.5 text-left text-slate-400">Date</th>
                <th className="px-2 py-1.5 text-right text-slate-400">Total</th>
                <th className="px-2 py-1.5 text-right text-slate-400">Paid</th>
                <th className="px-2 py-1.5 text-right text-slate-400">Balance</th>
                <th className="px-2 py-1.5 text-right text-slate-400">Days</th>
                <th className="px-2 py-1.5 text-left text-slate-400">Flags</th>
                <th className="px-2 py-1.5"></th>
              </tr></thead>
              <tbody>
                {drilldown.invoices.map(inv => (
                  <Fragment key={inv.invoice}>
                  <tr onClick={() => setOpenInvoice(openInvoice === inv.invoice ? null : inv.invoice)}
                    title="Show invoice detail"
                    className={`border-b border-slate-800/30 cursor-pointer ${openInvoice === inv.invoice ? 'bg-blue-500/10' : 'hover:bg-slate-700/10'}`}>
                    <td className="px-2 py-1.5 font-mono text-slate-300">
                      <ChevronDown size={11} className={`inline mr-1 transition-transform ${openInvoice === inv.invoice ? '' : '-rotate-90'}`} />
                      {inv.invoice}
                    </td>
                    <td className="px-2 py-1.5 text-slate-400">{fmtDate(inv.date)}</td>
                    <td className="px-2 py-1.5 text-right text-slate-300">{fmtCur(inv.total)}</td>
                    <td className="px-2 py-1.5 text-right text-emerald-400">{fmtCur(inv.paid)}</td>
                    <td className={`px-2 py-1.5 text-right font-medium ${inv.balance < 0 ? 'text-blue-400' : 'text-amber-400'}`}>{fmtCur(inv.balance)}</td>
                    <td className="px-2 py-1.5 text-right text-slate-400">{inv.daysOld}</td>
                    <td className="px-2 py-1.5">
                      <div className="flex gap-1">
                        {inv.flags.map(f => <span key={f} className={`text-[9px] px-1 py-0.5 rounded border ${f === 'skipped' ? 'border-red-500/30 text-red-300' : f === 'shortpaid' ? 'border-amber-500/30 text-amber-300' : 'border-blue-500/30 text-blue-300'}`}>{f}</span>)}
                      </div>
                    </td>
                    <td className="px-2 py-1.5 text-right"><InvoicePdfButtons invoice={inv.invoice} /></td>
                  </tr>
                  {openInvoice === inv.invoice && <ARInvoiceLines invoice={inv.invoice} colSpan={8} />}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showRollup && !isLoading && data && (
        <ArRollup customers={data.customers.filter(c => {
            // Same tab filters as the customer table, minus the rollup's own selection.
            if (area && (c.area || '').trim() !== area) return false;
            if (searchTerm) { const s = searchTerm.toLowerCase(); if (!c.name.toLowerCase().includes(s) && !String(c.code).includes(s)) return false; }
            if (minBal !== '' && !isNaN(parseFloat(minBal)) && c.balance < parseFloat(minBal)) return false;
            if (maxBal !== '' && !isNaN(parseFloat(maxBal)) && c.balance > parseFloat(maxBal)) return false;
            return true;
          })}
          dim={rollupDim} onDim={(d) => { setRollupDim(d); setRollupKey(''); }}
          selected={rollupKey} onSelect={setRollupKey} />
      )}

      {/* Customer Table */}
      {isLoading ? (
        <div className="flex items-center justify-center h-40"><RefreshCw className="w-6 h-6 text-blue-400 animate-spin" /></div>
      ) : (
        <div className="rounded-xl border border-slate-700/50 bg-slate-800/30 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-slate-700/50 bg-slate-800/50">
                  <th className="px-3 py-2.5 text-left text-slate-400 font-medium">Code</th>
                  <th className="px-3 py-2.5 text-left text-slate-400 font-medium cursor-pointer hover:text-white" onClick={() => handleSort('name')}>Customer {sortField === 'name' && (sortDir === 'desc' ? '↓' : '↑')}</th>
                  <th className="px-3 py-2.5 text-left text-slate-400 font-medium">Salesman</th>
                  <th className="px-3 py-2.5 text-right text-slate-400 font-medium cursor-pointer hover:text-white" onClick={() => handleSort('balance')}>Balance {sortField === 'balance' && (sortDir === 'desc' ? '↓' : '↑')}</th>
                  <th className="px-3 py-2.5 text-right text-emerald-400/70 font-medium">Current</th>
                  <th className="px-3 py-2.5 text-right text-blue-400/70 font-medium">31-60</th>
                  <th className="px-3 py-2.5 text-right text-amber-400/70 font-medium">61-90</th>
                  <th className="px-3 py-2.5 text-right text-red-400/70 font-medium cursor-pointer hover:text-white" onClick={() => handleSort('over90')}>90+ {sortField === 'over90' && (sortDir === 'desc' ? '↓' : '↑')}</th>
                  <th className="px-3 py-2.5 text-right text-slate-400 font-medium cursor-pointer hover:text-white" onClick={() => handleSort('avgAging')}>Avg Days {sortField === 'avgAging' && (sortDir === 'desc' ? '↓' : '↑')}</th>
                  <th className="px-3 py-2.5 text-left text-slate-400 font-medium">Flags</th>
                </tr>
              </thead>
              <tbody>
                {filteredCustomers.map(row => (
                  <tr key={row.code}
                    onClick={() => setSelectedCustomer(selectedCustomer === row.code ? null : row.code)}
                    className={`border-b border-slate-800/50 cursor-pointer transition-colors ${selectedCustomer === row.code ? 'bg-blue-500/10' : 'hover:bg-slate-700/20'}`}>
                    <td className="px-3 py-2 font-mono text-slate-500">{row.code}</td>
                    <td className="px-3 py-2 text-white font-medium max-w-[180px] truncate">{row.name}</td>
                    <td className="px-3 py-2 text-slate-400">{row.salesman}</td>
                    <td className="px-3 py-2 text-right font-medium text-white">{fmtCur(row.balance)}</td>
                    <td className="px-3 py-2 text-right text-emerald-400">{row.current > 0 ? fmtCur(row.current) : ''}</td>
                    <td className="px-3 py-2 text-right text-blue-400">{row.over30 > 0 ? fmtCur(row.over30) : ''}</td>
                    <td className="px-3 py-2 text-right text-amber-400">{row.over60 > 0 ? fmtCur(row.over60) : ''}</td>
                    <td className="px-3 py-2 text-right text-red-400">{(row.over90 + row.over120) > 0 ? fmtCur(row.over90 + row.over120) : ''}</td>
                    <td className="px-3 py-2 text-right text-slate-400">{row.avgAging > 0 ? `${row.avgAging}d` : ''}</td>
                    <td className="px-3 py-2"><FlagBadges row={row} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {filteredCustomers.length === 0 && !isLoading && (
            <div className="text-center py-8 text-slate-500 text-sm">No customers match the selected filters</div>
          )}
          <div className="px-3 py-2 border-t border-slate-700/30 text-xs text-slate-500">{filteredCustomers.length} customers</div>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// Main Analytics Page
// ══════════════════════════════════════════════════════════════

export default function AnalyticsPageV2({ onBack }: AnalyticsPageProps) {
  const { branding } = useAuthContext();
  const [activeTab, setActiveTab] = useState<'sales' | 'ar'>('sales');

  const { data: filters } = useQuery({
    queryKey: ['v2-analyticsFilters'],
    queryFn: () => api.getAnalyticsFilters(),
    staleTime: 5 * 60 * 1000,
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      <div className="max-w-[1400px] mx-auto">
        {/* Header */}
        <header className="border-b border-slate-800/50 backdrop-blur-sm bg-slate-950/50 sticky top-0 z-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <div className="flex flex-wrap items-center justify-between gap-y-2">
              <div className="flex items-center gap-3 min-w-0">
                {onBack && (
                  <button onClick={onBack} className="p-2 rounded-lg bg-slate-800/50 border border-slate-700/50 text-slate-400 hover:text-white transition-all">
                    <ArrowLeft size={18} />
                  </button>
                )}
                <div className="min-w-0">
                  <h1 className="text-lg font-semibold text-white truncate">{branding?.company_name || 'Delldata Reports'} — Analytics</h1>
                </div>
              </div>

              {/* Tab toggle */}
              <div className="flex rounded-lg border border-slate-700/50 overflow-hidden">
                <button onClick={() => setActiveTab('sales')}
                  className={`px-4 py-2 text-sm font-medium flex items-center gap-2 transition-all ${activeTab === 'sales' ? 'bg-blue-600 text-white' : 'bg-slate-800/50 text-slate-400 hover:text-white'}`}>
                  <BarChart3 size={15} /> Sales Detail
                </button>
                <button onClick={() => setActiveTab('ar')}
                  className={`px-4 py-2 text-sm font-medium flex items-center gap-2 transition-all ${activeTab === 'ar' ? 'bg-blue-600 text-white' : 'bg-slate-800/50 text-slate-400 hover:text-white'}`}>
                  <CreditCard size={15} /> AR Detail
                </button>
              </div>
            </div>
          </div>
        </header>

        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {activeTab === 'sales' ? <SalesDetailTab filters={filters} /> : <ARDetailTab filters={filters} />}
        </main>
      </div>
    </div>
  );
}